import { useTranslation } from '@rhino-project/core/i18n';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { Key } from 'react';

import { today, getLocalTimeZone } from '@internationalized/date';
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectIndicator,
  SelectPopover,
  ListBox,
  ListBoxItem,
  Label
} from '@heroui/react';
import { useModelIndex } from '@rhino-project/core/hooks';
import { seasonForDate, mostRecentEtsUpTo } from './seasonResolution';

type ExternalLinkedTeam = {
  external_team: number | string | null;
  external_team_season: number | string | null;
  team_name: string | null;
  season?: number | string | null;
};

type ExternalTeamValues = {
  external_team: Record<string, unknown> | null;
  external_linked_team: ExternalLinkedTeam;
  season?: unknown;
  [key: string]: unknown;
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type ExternalTeamRecord = Record<string, any>;

type ExternalTeamSelection =
  | (ExternalTeamRecord & {
      customOption?: boolean;
      display_name?: string;
      id?: number | string;
      external_team_seasons?: Array<{
        id: number | string;
        season: { id: number | string };
      }>;
    })
  | null;

type DropdownOption =
  | { type: 'team'; team: ExternalTeamRecord; name?: never }
  | { type: 'new'; name: string; team?: never };

const externalTeamIsSelected = (teamValues: ExternalTeamValues) =>
  teamValues?.external_linked_team?.external_team ||
  teamValues?.external_linked_team?.team_name;

type ExternalTeamsProps = {
  name: string;
  placeholder?: string;
  onlyTeamSeason?: boolean;
  teamValue: Record<string, unknown>;
  onTeamSelect: (values: ExternalTeamValues) => void;
  variant?: 'primary' | 'secondary';
  referenceDate?: string | null;
};

function ExternalTeams({
  name,
  placeholder,
  onlyTeamSeason = true,
  teamValue,
  onTeamSelect,
  variant = 'primary',
  referenceDate
}: ExternalTeamsProps) {
  const { t } = useTranslation();

  const inputClass = `input input--${variant} w-full`;

  const initialExternalTeamValues: ExternalTeamValues = {
    external_team: null,
    external_linked_team: {
      external_team: null,
      external_team_season: null,
      team_name: null
    },
    ...teamValue
  };

  const [externalTeamValues, setExternalTeamValues] =
    useState<ExternalTeamValues>(initialExternalTeamValues);
  const [teamSearch, setTeamSearch] = useState('');
  const [showDropdown, setShowDropdown] = useState(false);
  const [inputValue, setInputValue] = useState('');
  const [highlightIndex, setHighlightIndex] = useState(-1);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // seasonForDate picks the max itself, so the sort is only here to keep the
  // most recent seasons inside the page — an observation is never scouted
  // against a season older than the page can hold.
  const { isLoading: isSeasonLoading, resources: seasons } = useModelIndex(
    'season',
    { order: ['-starts_on'] }
  );

  // The reference season is the one that owns the observation's game date
  // (referenceDate), falling back to today. Resolved on the client from the
  // already-loaded season list so search/selection key off the right season.
  const season = useMemo(
    () =>
      seasonForDate(
        seasons?.results as
          { id: number | string; starts_on?: string | null }[] | undefined,
        referenceDate ?? today(getLocalTimeZone()).toString()
      ),
    [seasons, referenceDate]
  );

  const { isLoading, resources: external_teams } = useModelIndex(
    'external_team',
    {
      limit: 10,
      order: ['name'],
      search: teamSearch,
      queryOptions: { enabled: teamSearch.length > 0 }
    }
  );

  // Build the list of options for keyboard navigation
  const options: DropdownOption[] = useMemo(() => {
    const opts: DropdownOption[] = [];
    if (!isLoading && external_teams?.results) {
      external_teams.results.forEach((team) => {
        opts.push({ type: 'team', team });
      });
    }
    if (inputValue && !isLoading) {
      opts.push({ type: 'new', name: inputValue });
    }
    return opts;
  }, [isLoading, external_teams, inputValue]);

  // Close dropdown on outside click
  useEffect(() => {
    if (!showDropdown) return;
    const handleClickOutside = (e: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(e.target as Node)
      ) {
        setShowDropdown(false);
        setHighlightIndex(-1);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showDropdown]);

  const handleExternalTeamSelection = useCallback(
    (selected: ExternalTeamSelection) => {
      let updatedTeamValues: ExternalTeamValues;

      if (!selected || selected.customOption) {
        updatedTeamValues = {
          ...externalTeamValues,
          external_team: null,
          external_linked_team: {
            external_team: null,
            external_team_season: null,
            team_name: selected?.display_name || null,
            season:
              (externalTeamValues.season as string | number | null) ||
              season?.id ||
              null
          }
        };
      } else {
        const external_team = selected;
        const etss = external_team.external_team_seasons;
        const external_team_season = onlyTeamSeason
          ? mostRecentEtsUpTo(etss, season)
          : etss?.find(
              (ets: { season: { id: number | string } }) =>
                `${ets.season.id}` === `${season?.id}`
            );

        updatedTeamValues = {
          ...externalTeamValues,
          external_team: external_team as ExternalTeamRecord,
          external_linked_team: {
            external_team: external_team.id!,
            external_team_season: external_team_season?.id ?? null,
            team_name: null,
            season: external_team_season ? null : (season?.id ?? null)
          }
        };
      }

      setExternalTeamValues(updatedTeamValues);
      onTeamSelect(updatedTeamValues);
      setShowDropdown(false);
      setHighlightIndex(-1);
      setInputValue('');
    },
    [externalTeamValues, season, onlyTeamSeason, onTeamSelect]
  );

  const handleSeasonSelection = useCallback(
    (key: Key | null) => {
      if (key == null) return;
      const seasonId = parseInt(String(key));

      const external_team_season = (
        externalTeamValues.external_team as ExternalTeamRecord
      )?.external_team_seasons?.find(
        (ets: { id: number | string; season: { id: number } }) =>
          ets.season.id === seasonId
      );

      const updatedTeamValues: ExternalTeamValues = {
        ...externalTeamValues,
        external_linked_team: {
          ...externalTeamValues.external_linked_team,
          external_team_season: external_team_season?.id ?? null,
          season: external_team_season ? null : seasonId
        }
      };

      setExternalTeamValues(updatedTeamValues);
      onTeamSelect(updatedTeamValues);
    },
    [externalTeamValues, onTeamSelect]
  );

  const handleClear = useCallback(() => {
    setInputValue('');
    setTeamSearch('');
    handleExternalTeamSelection(null);
  }, [handleExternalTeamSelection]);

  const handleInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const val = e.target.value;
      setInputValue(val);
      setTeamSearch(val);
      setShowDropdown(val.length > 0);
      setHighlightIndex(-1);
    },
    []
  );

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (!showDropdown || options.length === 0) {
        if (e.key === 'Escape') {
          setShowDropdown(false);
        }
        return;
      }

      switch (e.key) {
        case 'ArrowDown':
          e.preventDefault();
          setHighlightIndex((prev) => Math.min(prev + 1, options.length - 1));
          break;
        case 'ArrowUp':
          e.preventDefault();
          setHighlightIndex((prev) => Math.max(prev - 1, 0));
          break;
        case 'Enter':
          e.preventDefault();
          if (highlightIndex >= 0 && highlightIndex < options.length) {
            const opt = options[highlightIndex];
            if (opt.type === 'team') {
              handleExternalTeamSelection(opt.team);
            } else {
              handleExternalTeamSelection({
                customOption: true,
                display_name: opt.name
              });
            }
          } else if (inputValue) {
            handleExternalTeamSelection({
              customOption: true,
              display_name: inputValue
            });
          }
          break;
        case 'Escape':
          setShowDropdown(false);
          setHighlightIndex(-1);
          break;
      }
    },
    [
      showDropdown,
      options,
      highlightIndex,
      inputValue,
      handleExternalTeamSelection
    ]
  );

  const handleBlur = useCallback(() => {
    setTimeout(() => {
      if (
        inputValue &&
        !externalTeamIsSelected(externalTeamValues) &&
        !showDropdown
      ) {
        handleExternalTeamSelection({
          customOption: true,
          display_name: inputValue
        });
      }
    }, 200);
  }, [
    inputValue,
    externalTeamValues,
    showDropdown,
    handleExternalTeamSelection
  ]);

  const selectedName = externalTeamIsSelected(externalTeamValues)
    ? ((externalTeamValues?.external_team as ExternalTeamRecord)
        ?.display_name as string) ||
      externalTeamValues?.external_linked_team?.team_name
    : '';

  const displayValue = selectedName || inputValue;

  // Resolve which season the Select should show. A pick is stored either as an
  // external_team_season (when the team has schedule data for that season) or as
  // a bare season id; map the former back to its season so the controlled value
  // reflects the choice, otherwise fall back to the reference season.
  const selectedEts =
    externalTeamValues?.external_linked_team?.external_team_season;
  const selectedEtsId =
    selectedEts && typeof selectedEts === 'object'
      ? (selectedEts as ExternalTeamRecord).id
      : selectedEts;
  const teamEtss = (externalTeamValues.external_team as ExternalTeamRecord)
    ?.external_team_seasons as
    { id: number | string; season: { id: number | string } }[] | undefined;
  const selectedSeasonId =
    externalTeamValues?.external_linked_team?.season ??
    teamEtss?.find((e) => `${e.id}` === `${selectedEtsId}`)?.season?.id ??
    season?.id ??
    null;

  // A team whose external data stops short of the game being scouted links
  // through its newest earlier season instead, and with no Season control on
  // screen (onlyTeamSeason) nothing says so. The label names that season, by
  // starts_on rather than identity so a link at or ahead of the game's own
  // season — which needs no announcing — stays quiet.
  const pastSeasonName = useMemo(() => {
    if (!onlyTeamSeason || !selectedName || selectedSeasonId == null) {
      return null;
    }

    const linked = (
      seasons?.results as
        | { id: number | string; name?: string | null; starts_on?: string }[]
        | undefined
    )?.find((s) => `${s.id}` === `${selectedSeasonId}`);
    const linkedStart = linked?.starts_on?.slice(0, 10);
    const gameStart = season?.starts_on?.slice(0, 10);
    if (!linkedStart || !gameStart) return null;

    return linkedStart < gameStart ? (linked?.name ?? null) : null;
  }, [onlyTeamSeason, selectedName, selectedSeasonId, season, seasons]);

  return (
    <div>
      <div className="relative" ref={dropdownRef}>
        <label className="label">
          {placeholder || name}
          {pastSeasonName && (
            <span className="text-warning"> ({pastSeasonName})</span>
          )}
        </label>
        <div className="flex items-center relative">
          <input
            ref={inputRef}
            type="text"
            value={displayValue}
            onChange={handleInputChange}
            onFocus={() => {
              if (inputValue) setShowDropdown(true);
            }}
            onBlur={handleBlur}
            onKeyDown={handleKeyDown}
            placeholder={t('frontend.messages.search_team')}
            className={inputClass}
          />
          {selectedName && (
            <button
              type="button"
              onClick={handleClear}
              className="absolute right-2 text-muted hover:text-foreground"
            >
              ×
            </button>
          )}
        </div>
        {showDropdown && options.length > 0 && (
          <div className="absolute top-full left-0 right-0 mt-1 bg-surface border border-separator rounded-3xl shadow-surface z-50 max-h-48 overflow-y-auto">
            {isLoading && (
              <div className="px-3 py-2 text-sm text-muted">
                {t('frontend.messages.searching')}
              </div>
            )}
            {options.map((opt, idx) => (
              <button
                key={opt.type === 'team' ? opt.team.id : `new-${opt.name}`}
                type="button"
                className={`w-full text-left px-3 py-2 text-sm hover:bg-surface-secondary ${
                  idx === highlightIndex ? 'bg-surface-secondary' : ''
                } ${
                  opt.type === 'new' &&
                  (external_teams?.results?.length ?? 0) > 0
                    ? 'border-t border-separator text-muted'
                    : ''
                }`}
                onMouseDown={() => {
                  if (opt.type === 'team') {
                    handleExternalTeamSelection(opt.team);
                  } else {
                    handleExternalTeamSelection({
                      customOption: true,
                      display_name: opt.name
                    });
                  }
                }}
                onMouseEnter={() => setHighlightIndex(idx)}
              >
                {opt.type === 'new'
                  ? t('frontend.messages.new_team', { name: opt.name })
                  : opt.team.display_name}
              </button>
            ))}
          </div>
        )}
      </div>
      {!onlyTeamSeason && (seasons?.total ?? 0) > 0 && (
        <Select
          className="mt-2"
          variant={variant}
          isDisabled={
            isSeasonLoading || !externalTeamIsSelected(externalTeamValues)
          }
          value={selectedSeasonId != null ? String(selectedSeasonId) : null}
          onChange={handleSeasonSelection}
        >
          <Label>{t('frontend.messages.season')}</Label>
          <SelectTrigger>
            <SelectValue />
            <SelectIndicator />
          </SelectTrigger>
          <SelectPopover>
            <ListBox items={seasons?.results || []}>
              {(s: ExternalTeamRecord) => (
                <ListBoxItem id={String(s.id)} key={s.id} textValue={s.name}>
                  {s.name}
                </ListBoxItem>
              )}
            </ListBox>
          </SelectPopover>
        </Select>
      )}
    </div>
  );
}

export default ExternalTeams;
